import { DataFactory, Parser, Store } from 'n3'
import { uniqueId } from 'lodash'
import { mapRecord } from '../../../utils/typescript'
import { z } from 'zod'

export type DataCategory =
  | 'string'
  | 'numeric'
  | 'boolean'
  | 'date'
  | 'duration'
  | 'binary'
  | 'resource'

export type BoolOp = 'and' | 'or'

export interface XsdTypeInfo {
  value: string
  category: DataCategory
  input?: string
  placeholder?: string
}

export const OPERATORS: Record<DataCategory, Record<string, string>> = {
  string: {
    equals:     'equals',
    notEquals:  'not equals',
    contains:   'contains',
    startsWith: 'starts with',
    endsWith:   'ends with',
    matches:    'matches /regex/',
    in:         'in',
    notIn:      'not in',
    isEmpty:    'is empty',
    isNotEmpty: 'is not empty',
  },
  numeric: {
    eq:        '=',
    ne:        '≠',
    lt:        '<',
    lte:       '≤',
    gt:        '>',
    gte:       '≥',
    between:   'between',
    in:        'in',
    notIn:     'not in',
    isNull:    'is null',
    isNotNull: 'is not null',
  },
  boolean: {
    isTrue:  'is true',
    isFalse: 'is false',
  },
  date: {
    eq:      'on',
    ne:      'not on',
    lt:      'before',
    lte:     'on or before',
    gt:      'after',
    gte:     'on or after',
    between: 'between',
  },
  duration: {
    eq:  '=',
    ne:  '≠',
    lt:  '<',
    lte: '≤',
    gt:  '>',
    gte: '≥',
  },
  binary: {
    equals:    'equals',
    notEquals: 'not equals',
    isNull:    'is null',
    isNotNull: 'is not null',
  },
  resource: {
    in:    'in',
    notIn: 'not in',
  },
}

export type OperatorKey = string

export const OperatorKeySchema = z.string() as z.ZodType<OperatorKey>

const PicklistItemSchema = z.object({
  value: z.string(),
  label: z.string(),
})

const ResourceFilterTargetSchema = z.object({
  type: z.literal('resource'),
  sparqlVar: z.string(),
  label: z.string(),
  predicate: z.string(),
  picklist: z.array(PicklistItemSchema),
})

const LiteralFilterTargetSchema = z.object({
  type: z.literal('literal'),
  sparqlVar: z.string(),
  label: z.string(),
  predicate: z.string(),
  datatype: z.string(),
  allowedOps: z.array(OperatorKeySchema).default([]),
}).refine(
  (f) => {
    const validOps = new Set(Object.keys(OPERATORS[typeInfo(f.datatype).category]))
    return f.allowedOps.every((op) => validOps.has(op))
  },
  { message: 'allowedOps contains operators invalid for this datatype' },
)

export const FilterTargetSchema = z.union([ResourceFilterTargetSchema, LiteralFilterTargetSchema])

export type ResourceFilterTarget = z.infer<typeof ResourceFilterTargetSchema>
export type LiteralFilterTarget = z.infer<typeof LiteralFilterTargetSchema>
export type FilterTarget = z.infer<typeof FilterTargetSchema>

export const FilterLabelsSchema = z.object({
  andLabel: z.string().optional(),
  orLabel: z.string().optional(),
  addGroupLabel: z.string().optional(),
  addConditionLabel: z.string().optional(),
  removeConditionLabel: z.string().optional(),
  removeGroupLabel: z.string().optional(),
  applyLabel: z.string().optional(),
})
export type FilterLabels = z.infer<typeof FilterLabelsSchema>

export const FILTER_CONFIG_SCHEMA = z.object({
  labels: FilterLabelsSchema.optional(),
  opLabels: z.record(z.string(), z.record(z.string(), z.string())).optional(),
  dynamicFilterTargetsConstructQuery: z.string(),
  stringOperatorsCaseInsensitive: z.boolean().optional()
})
export type FilterConfig = z.infer<typeof FILTER_CONFIG_SCHEMA>

export const FRESH_CONFIG: FilterConfig = { labels: {}, dynamicFilterTargetsConstructQuery: '' }

export interface FilterCondition {
  id: string
  type: 'condition'
  field: FilterTarget
  operator: string
  value: string | string[]
}

export interface FilterGroup {
  id: string
  type: 'group'
  operator: BoolOp
  negated: boolean
  children: FilterNode[]
}

export type FilterNode = FilterCondition | FilterGroup

export type NodePatch = Partial<FilterNode>

export type UpdateFn = (id: string, patch: NodePatch) => void
export type AddChildFn = (parentId: string, child: FilterNode) => void
export type RemoveFn = (id: string) => void

export function makeGroup(
  operator: BoolOp = 'and',
  children: FilterNode[] = [],
): FilterGroup {
  return {
    id: uniqueId('filterpp-group-'),
    type: 'group',
    operator,
    negated: false,
    children,
  }
}

export const XSD_NS = 'http://www.w3.org/2001/XMLSchema#'

const BARE_NUMERIC_LOCAL = [
  'integer',
  'int',
  'long',
  'short',
  'byte',
  'nonNegativeInteger',
  'positiveInteger',
  'negativeInteger',
  'nonPositiveInteger',
  'unsignedInt',
  'unsignedLong',
  'unsignedShort',
  'unsignedByte',
  'decimal',
]

export const BARE_NUMERIC_IRIS = new Set(BARE_NUMERIC_LOCAL.map((l) => XSD_NS + l))

export const XSD_TYPES: XsdTypeInfo[] = [
  { value: 'xsd:string', category: 'string' },
  { value: 'xsd:normalizedString', category: 'string' },
  { value: 'xsd:token', category: 'string' },
  { value: 'xsd:language', category: 'string' },
  { value: 'xsd:Name', category: 'string' },
  { value: 'xsd:NCName', category: 'string' },
  { value: 'xsd:NMTOKEN', category: 'string' },
  { value: 'xsd:ID', category: 'string' },
  { value: 'xsd:IDREF', category: 'string' },
  { value: 'xsd:anyURI', category: 'string', placeholder: 'https://...' },
  { value: 'xsd:QName', category: 'string', placeholder: 'prefix:local' },
  { value: 'xsd:decimal', category: 'numeric' },
  { value: 'xsd:float', category: 'numeric' },
  { value: 'xsd:double', category: 'numeric' },
  { value: 'xsd:integer', category: 'numeric' },
  { value: 'xsd:int', category: 'numeric' },
  { value: 'xsd:long', category: 'numeric' },
  { value: 'xsd:short', category: 'numeric' },
  { value: 'xsd:byte', category: 'numeric' },
  { value: 'xsd:nonNegativeInteger', category: 'numeric' },
  { value: 'xsd:positiveInteger', category: 'numeric' },
  { value: 'xsd:negativeInteger', category: 'numeric' },
  { value: 'xsd:nonPositiveInteger', category: 'numeric' },
  { value: 'xsd:unsignedInt', category: 'numeric' },
  { value: 'xsd:unsignedLong', category: 'numeric' },
  { value: 'xsd:unsignedShort', category: 'numeric' },
  { value: 'xsd:unsignedByte', category: 'numeric' },
  { value: 'xsd:boolean', category: 'boolean' },
  { value: 'xsd:date', category: 'date', input: 'date' },
  { value: 'xsd:dateTime', category: 'date', input: 'datetime-local' },
  { value: 'xsd:time', category: 'date', input: 'time' },
  { value: 'xsd:gYear', category: 'date', input: 'text', placeholder: 'YYYY' },
  { value: 'xsd:gYearMonth', category: 'date', input: 'month' },
  { value: 'xsd:gMonth', category: 'date', input: 'text', placeholder: '--MM' },
  {
    value: 'xsd:gMonthDay',
    category: 'date',
    input: 'text',
    placeholder: '--MM-DD',
  },
  { value: 'xsd:gDay', category: 'date', input: 'text', placeholder: '---DD' },
  { value: 'xsd:duration', category: 'duration', placeholder: 'P1Y2M3DT4H' },
  { value: 'xsd:base64Binary', category: 'binary' },
  { value: 'xsd:hexBinary', category: 'binary', placeholder: 'hex value' },
]

export const NO_VALUE_OPS = new Set([
  'isEmpty',
  'isNotEmpty',
  'isTrue',
  'isFalse',
  'isNull',
  'isNotNull',
])
export const MULTI_OPS = new Set(['in', 'notIn'])
export const RANGE_OPS = new Set(['between'])

export function typeInfo(t: string): XsdTypeInfo {
  return XSD_TYPES.find((x) => x.value === t) ?? XSD_TYPES[0]
}

/** Resolve display label for an operator on a given field, respecting global custom overrides. */
export function resolveOpLabel(
  field: FilterTarget,
  op: string,
  globalOpLabels?: FilterConfig['opLabels'],
): string {
  const cat: DataCategory = field.type === 'resource' ? 'resource' : typeInfo(field.datatype).category
  return globalOpLabels?.[cat]?.[op] ?? OPERATORS[cat][op] ?? op
}

const WISTOR_PREFIX = 'https://ont.wistor.nl/2026-04-29/widget/filterpp#'
const XSD_PREFIX = 'http://www.w3.org/2001/XMLSchema#'

export const CONSTRUCT_VOCAB = {
  rdfType:      'http://www.w3.org/1999/02/22-rdf-syntax-ns#type',
  FilterOption: WISTOR_PREFIX + 'FilterOption',
  predicate:    WISTOR_PREFIX + 'predicate',
  prefLabel:    'http://www.w3.org/2004/02/skos/core#prefLabel',
  datatype:     'http://www.w3.org/ns/shacl#datatype',
  picklist:     'http://www.w3.org/ns/shacl#in',
  value:        'http://www.w3.org/ns/shacl#value',
} as const

const V = mapRecord(CONSTRUCT_VOCAB, (_, v) => DataFactory.namedNode(v))

function xsdCurie(iri: string): string {
  return iri.startsWith(XSD_PREFIX) ? `xsd:${iri.slice(XSD_PREFIX.length)}` : iri
}

export function parseTurtleToFields(turtle: string): FilterTarget[] {
  const store = new Store()
  store.addQuads(new Parser().parse(turtle))

  const results: FilterTarget[] = []
  for (const filterOption of store.getSubjects(V.rdfType, V.FilterOption, null)) {

    const prefLabels = store.getObjects(filterOption, V.prefLabel, null)
    if (!prefLabels.length) console.warn(`no preflabels found for subject ${filterOption.value}, using URI instead`)
    if (prefLabels.length > 1) console.warn(`multiple prefLabels ${prefLabels.map(l => l.value).join(', ')} found for subject ${filterOption.value}`)
    const label = prefLabels.at(0)?.value ?? filterOption.value
    const sparqlVar = uniqueId('WISTOR_FILTERPP_')

    const predicates = store.getObjects(filterOption, V.predicate, null)
    if (predicates.length > 1) console.warn(`multiple predicates ${predicates.map(l => l.value).join(', ')} found for subject ${filterOption.value}`)
    const predicate = predicates.at(0)?.value

    if (!predicate) {
      console.error(`skipping: no predicate found for subject ${filterOption.value}`)
      continue
    }

    const picklistItems = store.getObjects(filterOption, V.value, null)
      .map((resource) => {
        const labels = store.getObjects(resource, V.prefLabel, null)

        let itemLabel: string
        if (!labels.length) {
          console.warn(`no statement found for \`<${resource.value}> <${V.prefLabel.value}> ?label .\`, using URI instead`)
          itemLabel = resource.value
        } else {
          itemLabel = labels[0].value
        }
        if (labels.length > 1) console.warn(`multiple statements found for \`<${resource.value}> <${V.prefLabel.value}> ?label .\`, using first one`)

        return { value: resource.value, label: itemLabel }
      })
      .sort((a, b) => a.label.localeCompare(b.label))

    const datatypes = store.getObjects(filterOption, V.datatype, null)

    if (picklistItems.length) {
      results.push({
        type: 'resource',
        sparqlVar,
        label,
        predicate,
        picklist: picklistItems,
      })

    } else if (datatypes.length) {
      const datatype = xsdCurie(datatypes[0].value)
      if (datatypes.length > 1) console.warn(`multiple datatypes ${datatypes.map(l => l.value).join(', ')} found for subject ${filterOption.value}`)
      const allowedOps = Object.keys(OPERATORS[typeInfo(datatype).category]) as OperatorKey[]

      results.push({
        type: 'literal',
        sparqlVar,
        label,
        predicate,
        datatype,
        allowedOps,
      })

    } else {
      console.error(`skipping: expected ${filterOption.value} to have a statement with either of the following predicates ${V.datatype} ${V.value}`)
    }

  }

  return results
}
